import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type PaymentMethodModalProps = {
  isOpen: boolean;
  reservationId: string;
  customerEmail: string;
  onClose: () => void;
  onPayWithCash: (reservationId: string) => Promise<void>;
  onPayWithProduct: (reservationId: string, purchaseIdPrefix: string, userEmail: string) => Promise<void>;
};

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  reservationId,
  customerEmail,
  onClose,
  onPayWithCash,
  onPayWithProduct,
}) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<'cash' | 'product' | null>(null);
  const [purchaseIdPrefix, setPurchaseIdPrefix] = useState('');
  const [userEmail, setUserEmail] = useState(customerEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens with new data
  React.useEffect(() => {
    if (isOpen) {
      setSelectedMethod(null);
      setPurchaseIdPrefix('');
      setUserEmail(customerEmail);
      setError(null);
    }
  }, [isOpen, customerEmail]);

  if (!isOpen) return null;

  const handleCashPayment = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onPayWithCash(reservationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('paymentMethod.failedToMarkAsPaid'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProductPayment = async () => {
    if (!purchaseIdPrefix || purchaseIdPrefix.length < 8) {
      setError(t('paymentMethod.purchaseIdMinLength'));
      return;
    }
    if (!userEmail) {
      setError(t('paymentMethod.emailRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onPayWithProduct(reservationId, purchaseIdPrefix, userEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('paymentMethod.failedToApplyProduct'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">{t('paymentMethod.title')}</h3>
        <p className="text-sm text-white/70 mb-6">{t('paymentMethod.chooseMethod')}</p>

        {error && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{error}</p>
          </div>
        )}

        {/* Payment Method Selection */}
        {selectedMethod === null && (
          <div className="space-y-3">
            <button
              onClick={() => handleCashPayment()}
              disabled={isSubmitting}
              className="w-full p-4 bg-surface-card hover:bg-surface-card-hover rounded-lg border border-border-subtle transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-confirm rounded-lg flex items-center justify-center text-white text-xl">
                  $
                </div>
                <div>
                  <div className="font-semibold text-ink-primary">{t('paymentMethod.cashPayment')}</div>
                  <div className="text-sm text-ink-secondary">{t('paymentMethod.cashPaymentDescription')}</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedMethod('product')}
              disabled={isSubmitting}
              className="w-full p-4 bg-surface-card hover:bg-surface-card-hover rounded-lg border border-border-subtle transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-primary rounded-lg flex items-center justify-center text-white text-xl">
                  P
                </div>
                <div>
                  <div className="font-semibold text-ink-primary">{t('paymentMethod.productPass')}</div>
                  <div className="text-sm text-ink-secondary">{t('paymentMethod.productPassDescription')}</div>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Product Payment Form */}
        {selectedMethod === 'product' && (
          <div className="space-y-4">
            <button
              onClick={() => setSelectedMethod(null)}
              className="text-sm text-brand-primary hover:text-brand-primary mb-2"
            >
              &larr; {t('paymentMethod.backToMethods')}
            </button>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t('paymentMethod.purchaseIdPrefix')} *
              </label>
              <input
                type="text"
                value={purchaseIdPrefix}
                onChange={(e) => setPurchaseIdPrefix(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder={t('paymentMethod.purchaseIdPlaceholder')}
                minLength={8}
              />
              <p className="text-xs text-white/60 mt-1">
                {t('paymentMethod.purchaseIdHint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t('paymentMethod.customerEmail')} *
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder={t('paymentMethod.emailPlaceholder')}
              />
            </div>

            <button
              onClick={handleProductPayment}
              disabled={isSubmitting || purchaseIdPrefix.length < 8 || !userEmail}
              className="w-full px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('paymentMethod.applying') : t('paymentMethod.applyProduct')}
            </button>
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full mt-4 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};
